import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * iOS-style interactive swipe-back gesture hook.
 * Uses direct DOM event listeners (non-passive touchmove) so the gesture
 * works even when inner content is scrollable.
 *
 * Only initiates when the touch starts within `edgeThreshold` pixels of the
 * left edge — mirrors iOS native back-swipe behaviour and avoids conflicts
 * with horizontal scroll or other gestures.
 *
 * @param {{ onBack: () => void, enabled?: boolean, edgeThreshold?: number }} options
 * @returns {{ swipeRef: React.Ref, swipeStyle: object, isSwiping: boolean }}
 */
export function useSwipeBack({ onBack, enabled = true, edgeThreshold = 28 }) {
  const [offset, setOffset] = useState(0);
  const [phase, setPhase] = useState('idle'); // 'idle'|'tracking'|'snapping'|'completing'
  const [targetEl, setTargetEl] = useState(null);

  const wrapperRef  = useRef(null);
  const startX      = useRef(0);
  const startY      = useRef(0);
  const lastX       = useRef(0);
  const lastT       = useRef(0);
  const tracking    = useRef(false);
  const decided     = useRef(false);
  const offsetRef   = useRef(0); // sync ref so handleEnd sees current value without stale closure
  const timerRef    = useRef(null); // stores the pending setTimeout ID so we can cancel it

  // Keep a stable ref to onBack so the effect doesn't re-run when onBack changes identity
  const onBackRef = useRef(onBack);
  useEffect(() => { onBackRef.current = onBack; }, [onBack]);

  // Helper: cancel any pending animation timer and reset to idle immediately
  const cancelTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Helper: full gesture state reset (used by touchcancel + disable path)
  const resetGesture = () => {
    cancelTimer();
    tracking.current  = false;
    decided.current   = true;
    offsetRef.current = 0;
    setOffset(0);
    setPhase('idle');
  };

  useEffect(() => {
    if (!enabled) {
      resetGesture();
      return;
    }
    const el = targetEl;
    if (!el) return;

    function handleStart(e) {
      const owner = e.target?.closest?.('[data-swipe-back-scope="true"]');
      if (owner && owner !== el) {
        tracking.current = false;
        decided.current = true;
        return;
      }

      // Cancel any lingering animation timer from a previous gesture
      cancelTimer();

      const t = e.touches[0];
      // Only start tracking if touch begins within the left edge zone
      if (t.clientX > edgeThreshold) {
        tracking.current = false;
        decided.current  = true; // already decided: not our gesture
        return;
      }
      startX.current    = t.clientX;
      startY.current    = t.clientY;
      lastX.current     = t.clientX;
      lastT.current     = Date.now();
      tracking.current  = false;
      decided.current   = false;
      offsetRef.current = 0;
    }

    function handleMove(e) {
      // Already decided this is NOT a horizontal swipe — let the browser handle it
      if (decided.current && !tracking.current) return;

      const t  = e.touches[0];
      const dx = t.clientX - startX.current;
      const dy = t.clientY - startY.current;

      // Determine direction on first 8px of movement
      if (!decided.current && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        decided.current  = true;
        tracking.current = Math.abs(dx) > Math.abs(dy) && dx > 0;
        if (tracking.current) setPhase('tracking');
      }

      if (!tracking.current) return;

      // Block browser scroll while we're tracking a horizontal swipe
      e.preventDefault();

      const raw = Math.max(0, dx);
      offsetRef.current = raw;
      setOffset(raw);
      lastX.current = t.clientX;
      lastT.current = Date.now();
    }

    function handleEnd() {
      if (!tracking.current) return;
      tracking.current = false;

      const dt       = Math.max(1, Date.now() - lastT.current);
      const velocity = Math.abs((lastX.current - startX.current) / dt); // px/ms
      const progress = offsetRef.current / window.innerWidth;

      if (progress > 0.38 || velocity > 0.45) {
        // Complete the back navigation
        setPhase('completing');
        setOffset(window.innerWidth);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          onBackRef.current();
          setOffset(0);
          setPhase('idle');
        }, 270);
      } else {
        // Spring snap-back
        setPhase('snapping');
        setOffset(0);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setPhase('idle');
        }, 350);
      }
    }

    // touchcancel fires when the OS interrupts the touch (e.g. incoming call,
    // notification banner, multi-finger gesture). Without it, tracking/phase/offset
    // stay dirty and the screen can remain partially translated.
    function handleCancel() {
      resetGesture();
    }

    const passiveCapture = { passive: true, capture: true };
    const activeCapture = { passive: false, capture: true };
    el.addEventListener('touchstart',  handleStart,  passiveCapture);
    el.addEventListener('touchmove',   handleMove,   activeCapture); // must be non-passive
    el.addEventListener('touchend',    handleEnd,    passiveCapture);
    el.addEventListener('touchcancel', handleCancel, passiveCapture);

    return () => {
      cancelTimer();
      el.removeEventListener('touchstart',  handleStart, true);
      el.removeEventListener('touchmove',   handleMove, true);
      el.removeEventListener('touchend',    handleEnd, true);
      el.removeEventListener('touchcancel', handleCancel, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, edgeThreshold, targetEl]); // onBack intentionally omitted — handled via onBackRef

  const setSwipeRef = useCallback((node) => {
    if (wrapperRef.current && wrapperRef.current !== node) {
      delete wrapperRef.current.dataset.swipeBackScope;
    }
    wrapperRef.current = node;
    if (node) node.dataset.swipeBackScope = 'true';
    setTargetEl(prev => (prev === node ? prev : node));
  }, []);

  const transition =
    phase === 'completing' ? 'transform 0.27s cubic-bezier(0.4,0,1,1)' :
    phase === 'snapping'   ? 'transform 0.34s cubic-bezier(0.34,1.2,0.64,1)' :
    'none';

  // Use transform:none when at rest — translateX(0) still creates a stacking context
  // and promotes the element to a GPU compositing layer, which causes a brief scale/zoom
  // artifact on iOS Safari when the screen first paints. Same pattern as swipeable-tabs.jsx.
  const isMoving = offset !== 0 || phase !== 'idle';
  const swipeStyle = {
    transform:  isMoving ? `translateX(${offset}px)` : 'none',
    transition,
    willChange: isMoving ? 'transform' : 'auto',
    boxShadow:  offset > 0 ? '-6px 0 24px rgba(0,0,0,0.14)' : 'none',
  };

  return {
    swipeRef: setSwipeRef,
    swipeStyle,
    isSwiping: phase === 'tracking',
  };
}
